import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Trip } from "../../../../store/admin/tripsStore";
import {
  useVehiclesSocketStore,
  type VehicleSocket,
} from "../../../../store/admin/vehiclesSocketStore";
import { useTripTrackingStore } from "../../../../store/admin/tripTrackingStore";
import { useOpenChat } from "../../../../pages/support/hooks/useOpenChat";
import NewConversationModal from "../../../../pages/support/components/NewConversationModal";
import { collectTripParticipantSeed } from "../../../../pages/support/utils/tripChatHelpers";
import { activeThemeId } from "../../../../config/theme";

const isChatEnabled = activeThemeId === "central-gps";

interface TripCardProps {
  trip: Trip;
  vehicles: VehicleSocket[];
  isDarkMode: boolean;
  themeClasses: {
    cardBg: string;
    cardBorder: string;
    text: string;
    textSecondary: string;
    buttonHover: string;
  };
}

function findAttachedVehicle(
  trip: Trip,
  vehicles: VehicleSocket[],
): VehicleSocket | undefined {
  if (trip.vehicle_id) {
    const match = vehicles.find((v) => v.vehicle_id === trip.vehicle_id);
    if (match) return match;
  }
  if (trip.remolque_id) {
    const match = vehicles.find((v) => v.remolque_id === trip.remolque_id);
    if (match) return match;
  }
  for (const a of trip.assignments || []) {
    if (a.device_id && (a.status === "active" || a.status === "pending")) {
      const match = vehicles.find((v) => v.id === a.device_id);
      if (match) return match;
    }
  }
  return undefined;
}

const EVENT_LABELS: Record<string, string> = {
  VIAJE_INICIADO: "Viaje iniciado",
  DEMORA_EN_INICIO: "Demora en inicio",
  INICIO_TARDIO: "Inicio tardío",
  DESVIO_DE_RUTA: "Fuera de ruta",
  SIGUE_FUERA_DE_RUTA: "Sigue fuera de ruta",
  REGRESO_A_RUTA: "Regresó a la ruta",
  VIAJE_COMPLETADO: "Viaje completado",
};

function TripCardComponent({
  trip,
  vehicles,
  isDarkMode,
  themeClasses,
}: TripCardProps) {
  const { t } = useTranslation("lite");
  const setFocusedVehicleId = useVehiclesSocketStore(
    (state) => state.setFocusedVehicleId,
  );
  const { tryOpenExistingTripChat, goToConversation, isReady } = useOpenChat();
  const [showTripChatModal, setShowTripChatModal] = useState(false);
  const [isOpeningChat, setIsOpeningChat] = useState(false);

  const attachedVehicle = findAttachedVehicle(trip, vehicles);

  // Pre-resolvemos los conductores atados al viaje para preseleccionarlos
  // como participantes del chat. Combinamos dos fuentes:
  //   - `assignment.driver.user_id` que ya viene poblado del backend
  //     (`tripsController._resolveDriversForVehicleIds`) — match exacto por
  //     id, robusto aunque el email del driver no esté sincronizado con el
  //     del user.
  //   - El email del realtime (`vehiclesSocketStore.driver.email`) como
  //     fallback para trips antiguos no enriquecidos.
  // Recomputamos solo cuando cambia el trip o el store de sockets.
  const tripParticipantSeed = useMemo(
    () => collectTripParticipantSeed(trip, vehicles),
    [trip, vehicles],
  );

  // Real-time tracking state (indexed by trip_id)
  const tripTracking = useTripTrackingStore(
    (state) => state.byTripId[trip.id] || null,
  );

  const handleClick = () => {
    if (attachedVehicle) {
      setFocusedVehicleId(attachedVehicle.id);
    }
  };

  const handleOpenChat = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isReady || isOpeningChat) return;
    setIsOpeningChat(true);
    try {
      const opened = await tryOpenExistingTripChat(trip.id);
      if (!opened) {
        // No existe el chat: dejamos que el usuario elija participantes
        // con el modal preconfigurado en modo "viaje".
        setShowTripChatModal(true);
      }
    } finally {
      setIsOpeningChat(false);
    }
  };

  const hasAttached = !!attachedVehicle;
  const vehicleName =
    attachedVehicle?.vehicle?.name ||
    trip.vehicle?.name ||
    trip.vehicle?.plate ||
    null;
  const remolqueName =
    trip.remolque?.name || trip.remolque?.plate || null;
  const routeText =
    trip.origin && trip.destiny ? `${trip.origin} → ${trip.destiny}` : null;

  // Tracking status badge config
  const isDeviated = tripTracking?.is_deviated ?? false;
  const lastEvent = tripTracking?.last_event;
  const trackingStatus = tripTracking?.trip_status;

  const hasPendingAssignment = trip.assignments?.some(
    (a) => a.status === "pending",
  );

  let statusIcon: string;
  let statusColor: string;
  let statusBg: string;
  let statusLabel: string;

  if (trackingStatus === "COMPLETED" && hasPendingAssignment) {
    statusIcon = "ri-time-line";
    statusColor = "text-yellow-500";
    statusBg = isDarkMode ? "bg-yellow-500/15" : "bg-yellow-100";
    statusLabel = t("sidebar.tripPending", "Pendiente");
  } else if (isDeviated) {
    statusIcon = "ri-alert-line";
    statusColor = "text-red-500";
    statusBg = isDarkMode ? "bg-red-500/15" : "bg-red-100";
    statusLabel = EVENT_LABELS[lastEvent || "DESVIO_DE_RUTA"] || "Fuera de ruta";
  } else if (trackingStatus === "COMPLETED") {
    statusIcon = "ri-checkbox-circle-line";
    statusColor = "text-green-500";
    statusBg = isDarkMode ? "bg-green-500/15" : "bg-green-100";
    statusLabel = "Completado";
  } else if (trackingStatus === "IN_PROGRESS") {
    statusIcon = "ri-play-circle-line";
    statusColor = "text-blue-500";
    statusBg = isDarkMode ? "bg-blue-500/15" : "bg-blue-100";
    statusLabel = EVENT_LABELS[lastEvent || "VIAJE_INICIADO"] || "En ruta";
  } else if (hasPendingAssignment) {
    statusIcon = "ri-time-line";
    statusColor = "text-yellow-500";
    statusBg = isDarkMode ? "bg-yellow-500/15" : "bg-yellow-100";
    statusLabel = t("sidebar.tripPending", "Pendiente");
  } else {
    statusIcon = "ri-route-line";
    statusColor = "text-blue-500";
    statusBg = isDarkMode ? "bg-blue-500/20" : "bg-blue-100";
    statusLabel = t("sidebar.activeTrip", "Viaje en curso");
  }

  // Time since last event
  const lastEventTime = tripTracking?.last_event_time;
  const timeAgo = (() => {
    if (!lastEventTime) return null;
    const totalMin = Math.round((Date.now() - lastEventTime) / 60000);
    if (totalMin < 0) return t("sidebar.justNow", "Ahora");
    if (totalMin < 60) return `${totalMin} min`;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  })();

  return (
    <div
      onClick={handleClick}
      className={`p-3 ${themeClasses.cardBg} rounded-lg border ${themeClasses.cardBorder} transition-all duration-200 relative ${hasAttached ? "cursor-pointer hover:border-blue-500/60" : "cursor-default opacity-70"}`}
      title={
        hasAttached
          ? t("sidebar.clickToLocateOnMap", "Click para ubicar en el mapa")
          : t("sidebar.noAttachedUnit", "Sin unidad asignada")
      }
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <div
          className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${statusBg}`}
        >
          <i className={`${statusIcon} ${statusColor} text-sm`}></i>
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-[10px] font-semibold uppercase tracking-wide ${statusColor} leading-none mb-0.5`}>
            {statusLabel}
          </p>
          <p
            className={`text-sm font-semibold truncate ${themeClasses.text}`}
            title={trip.title || trip.id}
          >
            {trip.title || trip.id}
          </p>
        </div>
        {timeAgo && tripTracking && (
          <span className={`text-[10px] ${themeClasses.textSecondary} flex-shrink-0`}>
            {timeAgo}
          </span>
        )}
        {isChatEnabled && (
          <button
            type="button"
            onClick={handleOpenChat}
            disabled={!isReady || isOpeningChat}
            title={t("sidebar.openTripChat", "Abrir chat del viaje")}
            className={`flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center transition-colors ${
              isDarkMode
                ? "bg-dark-card hover:bg-primary-500/20 text-gray-400 hover:text-primary-400"
                : "bg-gray-100 hover:bg-primary-500/10 text-gray-500 hover:text-primary-600"
            } ${!isReady || isOpeningChat ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <i
              className={`${isOpeningChat ? "ri-loader-4-line animate-spin" : "ri-chat-3-line"} text-sm`}
            ></i>
          </button>
        )}
      </div>

      {/* Route */}
      {(routeText || tripTracking?.route_name) && (
        <div className="flex items-start gap-1.5 mb-2">
          <i className={`ri-map-pin-line text-xs mt-0.5 ${themeClasses.textSecondary}`}></i>
          <p
            className={`text-xs ${themeClasses.textSecondary} truncate`}
            title={routeText || tripTracking?.route_name || ""}
          >
            {routeText || tripTracking?.route_name}
          </p>
        </div>
      )}

      {/* Last tracking message */}
      {tripTracking?.message && (
        <div className={`mb-2 px-2 py-1 rounded text-[11px] ${
          isDeviated
            ? isDarkMode ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"
            : isDarkMode ? "bg-dark-card text-gray-400" : "bg-gray-100 text-gray-600"
        } truncate`}
          title={tripTracking.message}
        >
          {tripTracking.message}
        </div>
      )}

      {/* Attached vehicle + remolque */}
      <div className="flex items-center gap-2 flex-wrap">
        {vehicleName && (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${isDarkMode ? "bg-dark-card text-gray-300" : "bg-gray-100 text-gray-700"}`}
            title={t("sidebar.vehicle", "Vehículo")}
          >
            <i className="ri-truck-line text-[11px]"></i>
            <span className="truncate max-w-[120px]">{vehicleName}</span>
          </span>
        )}
        {remolqueName && (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${isDarkMode ? "bg-dark-card text-gray-300" : "bg-gray-100 text-gray-700"}`}
            title={t("sidebar.trailer", "Remolque")}
          >
            <i className="ri-caravan-line text-[11px]"></i>
            <span className="truncate max-w-[120px]">{remolqueName}</span>
          </span>
        )}
        {!vehicleName && !remolqueName && (
          <span className={`text-[11px] italic ${themeClasses.textSecondary}`}>
            {t("sidebar.noAttachedUnit", "Sin unidad asignada")}
          </span>
        )}
      </div>

      {showTripChatModal && (
        <NewConversationModal
          initialMode="viaje"
          initialTripId={trip.id}
          initialParticipantEmails={tripParticipantSeed.emails}
          initialParticipantUserIds={tripParticipantSeed.userIds}
          onClose={() => setShowTripChatModal(false)}
          onCreated={(conversationId) => {
            setShowTripChatModal(false);
            goToConversation(conversationId);
          }}
        />
      )}
    </div>
  );
}

export default memo(TripCardComponent);
